/**
 * Calculs de facture (6.1, 7.6).
 *
 * Quatre régimes : taux normal à 20 %, taux réduit à 5,5 % (importation et
 * première vente par l'artiste), TVA sur la marge des biens d'occasion,
 * exonération. Tout entre et sort en centimes entiers.
 *
 * DÉCISION (ARTO-51) — LA TVA S'ARRONDIT UNE FOIS, PAR CATÉGORIE ET PAR TAUX.
 * C'est la règle de l'EN 16931 (BT-117 = BT-116 × taux, arrondi au centime), et
 * celle de `@artosera/documents`, qui produit le XML Factur-X et le PDF. Elle
 * était ligne à ligne ici : une facture de plusieurs lignes dans une même
 * catégorie pouvait porter dans l'API un centime de moins que son document.
 * La TVA d'une catégorie est ensuite RÉPARTIE sur ses lignes (plus forts
 * restes), pour que chaque ligne garde un montant indicatif et que les lignes
 * somment exactement la catégorie. `calculerLigne`, qui calcule une ligne
 * seule, est une facture d'une ligne : l'arrondi y est le même.
 */
import { AppError } from '../errors.js';
import { formatPrice } from '../format/index.js';
import { arrondirCentimes } from './arrondi.js';
import { BAREME_DROIT_DE_SUITE, calculerDroitDeSuite } from './droit-de-suite.js';
import { MENTIONS_REGIME, MENTION_DROIT_DE_SUITE, POURCENTAGES } from './mentions.js';
import { DESCRIPTION_REGIMES } from './regimes.js';
export * from './arrondi.js';
export * from './droit-de-suite.js';
export * from './identifiants.js';
export * from './mentions.js';
export * from './regimes.js';
/**
 * Le plafond d'un montant de facture : un milliard dans la devise (recette
 * d'octobre 2026, défaut 7). Au-delà, rien de réel ne se facture, et les
 * produits intermédiaires — une base par un taux en points de base — sortent
 * des entiers exacts du flottant : l'API et le XML Factur-X divergeaient sur
 * 9 007 199 254 740 991 centimes × 9 999. Il borne chaque ligne et le total
 * hors taxes d'une facture.
 */
export const MONTANT_MAXIMAL_CENTIMES = 100_000_000_000;
/** Un montant négatif refusé, en nommant la ligne et le champ. */
function exigerMontantPositif(montant, designation, quoi) {
    if (montant >= 0)
        return;
    throw new AppError('validation.invalide', `Le ${quoi} de la ligne « ${designation} » est négatif : un montant de facture est ` +
        'positif ou nul. Une remise se saisit en pourcentage, une reprise par un avoir.', { raison: 'montant_negatif', designation, champ: quoi });
}
/** Un montant au-delà du plafond refusé, plafond écrit dans la devise de la facture. */
function exigerSousLePlafond(montant, quoi, devise, designation) {
    if (montant <= MONTANT_MAXIMAL_CENTIMES)
        return;
    throw new AppError('validation.invalide', `Le montant de ${quoi} dépasse le plafond de ` +
        `${formatPrice(MONTANT_MAXIMAL_CENTIMES, devise, 'fr')} : vérifiez le prix unitaire et ` +
        'la quantité.', {
        raison: 'montant_hors_bornes',
        plafondCentimes: MONTANT_MAXIMAL_CENTIMES,
        ...(designation === undefined ? {} : { designation }),
    });
}
/** Calcule une ligne : base, TVA, total, marge et droit de suite. */
export function calculerLigne(ligne, bareme = BAREME_DROIT_DE_SUITE, devise = 'EUR') {
    const quantite = ligne.quantite ?? 1;
    if (!Number.isInteger(quantite) || quantite < 1) {
        throw new AppError('validation.invalide', 'La quantité d’une ligne est un entier positif.', {
            designation: ligne.designation,
        });
    }
    if (!Number.isInteger(ligne.prixUnitaireCentimes) ||
        (ligne.prixAchatCentimes !== undefined && !Number.isInteger(ligne.prixAchatCentimes))) {
        throw new AppError('validation.invalide', 'Un prix s’exprime en centimes entiers.', {
            designation: ligne.designation,
        });
    }
    exigerMontantPositif(ligne.prixUnitaireCentimes, ligne.designation, 'prix unitaire');
    if (ligne.prixAchatCentimes !== undefined) {
        exigerMontantPositif(ligne.prixAchatCentimes, ligne.designation, 'prix d’acquisition');
    }
    const description = DESCRIPTION_REGIMES[ligne.regime];
    const total = ligne.prixUnitaireCentimes * quantite;
    exigerSousLePlafond(total, `la ligne « ${ligne.designation} »`, devise, ligne.designation);
    if (ligne.regime === 'marge') {
        if (ligne.prixAchatCentimes === undefined) {
            throw new AppError('tva.prix_achat_requis', 'La TVA sur la marge demande le prix d’acquisition. Renseignez-le sur la fiche de l’œuvre.', { designation: ligne.designation });
        }
        const marge = total - ligne.prixAchatCentimes * quantite;
        if (marge < 0) {
            throw new AppError('tva.marge_negative', 'Le prix de vente est inférieur au prix d’acquisition. Vérifiez les deux montants.', { designation: ligne.designation, marge });
        }
        // La TVA est comprise dans la marge : marge × taux / (100 % + taux).
        const tvaSurMarge = arrondirCentimes((marge * description.taux) / (10_000 + description.taux));
        const droitDeSuite = droitDeSuiteDeLaLigne(ligne, quantite, bareme);
        return {
            designation: ligne.designation,
            quantite,
            regime: ligne.regime,
            categorieTva: description.categorie,
            tauxPointsDeBase: 0,
            baseCentimes: total,
            tvaCentimes: 0,
            totalTtcCentimes: total,
            margeCentimes: marge,
            tvaSurMargeCentimes: tvaSurMarge,
            droitDeSuiteCentimes: droitDeSuite.montant,
            assietteDroitDeSuiteCentimes: droitDeSuite.assiette,
        };
    }
    const tva = arrondirCentimes((total * description.taux) / 10_000);
    const droitDeSuite = droitDeSuiteDeLaLigne(ligne, quantite, bareme);
    return {
        designation: ligne.designation,
        quantite,
        regime: ligne.regime,
        categorieTva: description.categorie,
        tauxPointsDeBase: description.taux,
        baseCentimes: total,
        tvaCentimes: tva,
        totalTtcCentimes: total + tva,
        droitDeSuiteCentimes: droitDeSuite.montant,
        assietteDroitDeSuiteCentimes: droitDeSuite.assiette,
    };
}
/**
 * L'assiette du droit de suite d'UNE œuvre, d'un exemplaire : son prix de vente
 * hors taxes (art. L122-8 et R122-5 du CPI ; recette d'octobre 2026,
 * défaut 5).
 *
 * En TVA sur la marge, le prix saisi est celui que paie l'acheteur, et la seule
 * taxe qu'il contient est la TVA due SUR LA MARGE : le prix hors taxes est le
 * prix diminué de cette TVA-là. L'ancien calcul ramenait le prix hors taxes au
 * taux plein, comme si la TVA portait sur tout le prix : 16 000 € vendus pour
 * 9 760 € d'achat donnaient une assiette de 13 333,33 € (533,33 € de droit de
 * suite) au lieu de 14 960 € (598,40 €).
 */
export function assietteUnitaireDroitDeSuite(ligne) {
    const description = DESCRIPTION_REGIMES[ligne.regime];
    if (!description.prixTtc)
        return ligne.prixUnitaireCentimes;
    if (ligne.prixAchatCentimes === undefined) {
        throw new AppError('tva.prix_achat_requis', 'La TVA sur la marge demande le prix d’acquisition. Renseignez-le sur la fiche de l’œuvre.');
    }
    const marge = ligne.prixUnitaireCentimes - ligne.prixAchatCentimes;
    if (marge < 0) {
        throw new AppError('tva.marge_negative', 'Le prix de vente est inférieur au prix d’acquisition. Vérifiez les deux montants.', { marge });
    }
    const tvaSurMarge = arrondirCentimes((marge * description.taux) / (10_000 + description.taux));
    return ligne.prixUnitaireCentimes - tvaSurMarge;
}
/**
 * Le droit de suite d'une ligne : le barème s'applique à CHAQUE exemplaire
 * (recette d'octobre 2026, défaut 6), parce que chaque exemplaire vendu est la
 * vente d'une œuvre — seuil de 750 € et plafond de 12 500 € compris. Appliqué
 * à la quantité totale, deux estampes de 500 € devaient 40 €, et le barème
 * dégressif sous-estimait trois tirages de 60 000 €.
 */
function droitDeSuiteDeLaLigne(ligne, quantite, bareme) {
    if (ligne.droitDeSuite !== true)
        return { montant: 0, assiette: 0 };
    const assiette = assietteUnitaireDroitDeSuite(ligne);
    const parExemplaire = calculerDroitDeSuite(assiette, bareme);
    if (parExemplaire === 0)
        return { montant: 0, assiette: 0 };
    return { montant: parExemplaire * quantite, assiette: assiette * quantite };
}
/** Calcule une facture entière, avec ses totaux par catégorie de TVA. */
export function calculerFacture(facture) {
    if (facture.lignes.length === 0) {
        throw new AppError('facture.aucune_ligne', 'Une facture porte au moins une ligne.');
    }
    const bareme = facture.bareme ?? BAREME_DROIT_DE_SUITE;
    const devise = facture.devise ?? 'EUR';
    const calculees = facture.lignes.map((ligne) => calculerLigne(ligne, bareme, devise));
    exigerSousLePlafond(calculees.reduce((somme, ligne) => somme + ligne.baseCentimes, 0), 'la facture', devise);
    // Les bases, par catégorie ET par taux (le régime distingue deux régimes de
    // même catégorie et de même taux : la marge et l'exonération, toutes deux
    // `E` à 0 %, que les mentions et l'export doivent séparer).
    const categories = new Map();
    calculees.forEach((ligne, rang) => {
        const cle = `${ligne.categorieTva}:${ligne.regime}:${String(ligne.tauxPointsDeBase)}`;
        const courant = categories.get(cle);
        categories.set(cle, {
            categorie: ligne.categorieTva,
            regime: ligne.regime,
            tauxPointsDeBase: ligne.tauxPointsDeBase,
            baseCentimes: (courant?.baseCentimes ?? 0) + ligne.baseCentimes,
            tvaCentimes: 0,
            rangs: [...(courant?.rangs ?? []), rang],
        });
    });
    // La TVA de chaque catégorie, arrondie UNE fois, puis répartie sur ses
    // lignes. En marge, la TVA n'est pas facturée : elle vaut 0, et la TVA sur la
    // marge reste un montant interne, ligne par ligne.
    const tvaParLigne = calculees.map(() => 0);
    const parCategorieTva = [];
    for (const { rangs, ...categorie } of categories.values()) {
        const facturee = calculees[rangs[0] ?? 0]?.regime !== 'marge';
        const tva = facturee
            ? arrondirCentimes((categorie.baseCentimes * categorie.tauxPointsDeBase) / 10_000)
            : 0;
        repartir(tva, rangs.map((rang) => calculees[rang]?.baseCentimes ?? 0)).forEach((part, index) => {
            tvaParLigne[rangs[index] ?? 0] = part;
        });
        parCategorieTva.push({ ...categorie, tvaCentimes: tva });
    }
    const lignes = calculees.map((ligne, rang) => {
        const tva = tvaParLigne[rang] ?? 0;
        return { ...ligne, tvaCentimes: tva, totalTtcCentimes: ligne.baseCentimes + tva };
    });
    const totalHt = lignes.reduce((somme, ligne) => somme + ligne.baseCentimes, 0);
    const totalTva = parCategorieTva.reduce((somme, categorie) => somme + categorie.tvaCentimes, 0);
    const droitDeSuite = lignes.reduce((somme, ligne) => somme + ligne.droitDeSuiteCentimes, 0);
    const tvaSurMarge = lignes.reduce((somme, ligne) => somme + (ligne.tvaSurMargeCentimes ?? 0), 0);
    const totalTtc = totalHt + totalTva + (facture.droitDeSuiteAChargeAcheteur === true ? droitDeSuite : 0);
    return {
        lignes,
        totalHtCentimes: totalHt,
        totalTvaCentimes: totalTva,
        totalTtcCentimes: totalTtc,
        droitDeSuiteCentimes: droitDeSuite,
        tvaSurMargeCentimes: tvaSurMarge,
        parCategorieTva,
        mentions: mentionsDeLaFacture(lignes, droitDeSuite),
    };
}
/**
 * Répartit un montant entier sur des bases, au prorata, par la méthode des
 * plus forts restes : les parts somment exactement le montant, et chacune est
 * au plus à un centime de sa part exacte.
 */
function repartir(montant, bases) {
    const total = bases.reduce((somme, base) => somme + base, 0);
    if (total === 0)
        return bases.map((_, index) => (index === 0 ? montant : 0));
    const exactes = bases.map((base) => (montant * base) / total);
    const parts = exactes.map((exacte) => Math.trunc(exacte));
    let reste = montant - parts.reduce((somme, part) => somme + part, 0);
    const ordre = exactes
        .map((exacte, index) => ({ index, fraction: Math.abs(exacte - Math.trunc(exacte)) }))
        .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
    const pas = Math.sign(reste);
    for (let i = 0; reste !== 0; i = (i + 1) % ordre.length) {
        const cible = ordre[i]?.index ?? 0;
        parts[cible] = (parts[cible] ?? 0) + pas;
        reste -= pas;
    }
    return parts;
}
function mentionsDeLaFacture(lignes, droitDeSuiteCentimes) {
    const regimes = new Set(lignes.map((ligne) => ligne.regime));
    const mentions = [];
    for (const regime of regimes) {
        const mention = MENTIONS_REGIME[regime];
        if (mention !== undefined)
            mentions.push(mention);
    }
    if (droitDeSuiteCentimes > 0)
        mentions.push(MENTION_DROIT_DE_SUITE);
    return mentions;
}
/** Le taux d'un régime en pourcentage, pour l'impression. */
export function pourcentage(regime) {
    return POURCENTAGES[regime];
}
/** Le libellé d'un taux dans la langue du document : « 20 % », « 5.5% ». */
export function libelleTaux(regime, lang) {
    const valeur = POURCENTAGES[regime];
    return lang === 'fr' ? `${valeur.toString().replace('.', ',')} %` : `${valeur.toString()}%`;
}
//# sourceMappingURL=index.js.map